import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiArrowRight, FiAlertCircle, FiMusic, FiCpu, FiArrowLeft } = FiIcons;

const AccessScreen = ({ onAccess }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim().toLowerCase() === '#vineriazi') {
      setError(false);
      onAccess();
    } else {
      setError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 relative z-10 w-full">
      <button 
        onClick={() => navigate('/')}
        className="absolute top-8 left-8 flex items-center gap-2 text-stone-500 hover:text-orange-600 transition-colors font-medium"
      >
        <SafeIcon icon={FiArrowLeft} /> Back to Home
      </button>

      <motion.div 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.5 }}
        className="flex items-center justify-center gap-3 mb-8"
      >
        <div className="relative">
          <SafeIcon icon={FiMusic} className="text-4xl text-orange-600" />
          <SafeIcon icon={FiCpu} className="text-xl text-amber-500 absolute -bottom-1 -right-1 bg-orange-50 rounded-full p-0.5" />
        </div>
        <h1 className="text-4xl font-extrabold text-stone-800 tracking-tight">
          Suno<span className="text-orange-600">Sync</span>
        </h1>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white/80 backdrop-blur-md p-8 sm:p-10 rounded-3xl shadow-xl border border-orange-100 max-w-md w-full"
      >
        <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
          <SafeIcon icon={FiLock} className="text-3xl" />
        </div>
        
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Restricted Access</h2>
          <p className="text-stone-500 text-sm">
            Please enter your invite code to access the Suno V5.5 Prompt Architect.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <motion.div
            animate={isShaking ? { x: [-10, 10, -10, 10, 0] } : {}}
            transition={{ duration: 0.4 }}
            className="space-y-2"
          >
            <div className="relative">
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError(false);
                }}
                placeholder="Enter access code..."
                className={`w-full px-5 py-4 rounded-2xl border-2 transition-all outline-none font-medium text-center text-lg tracking-wider
                  ${error 
                    ? 'border-red-300 bg-red-50 text-red-900 focus:border-red-500 focus:ring-4 focus:ring-red-500/20' 
                    : 'border-stone-200 bg-stone-50 text-stone-800 focus:border-orange-500 focus:bg-white focus:ring-4 focus:ring-orange-500/20'
                  }`}
              />
            </div>
            
            {error && (
              <motion.p 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="text-red-500 text-sm flex items-center justify-center gap-1.5 font-medium"
              >
                <SafeIcon icon={FiAlertCircle} /> Invalid access code
              </motion.p>
            )}
          </motion.div>

          <button
            type="submit"
            className="w-full py-4 bg-stone-900 text-white rounded-2xl font-bold hover:bg-stone-800 transition-all active:scale-[0.98] flex items-center justify-center gap-2 shadow-lg shadow-stone-200"
          >
            Unlock Access <SafeIcon icon={FiArrowRight} />
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default AccessScreen;